import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function AdminSetup() {
  const navigate = useNavigate();
  const [token] = useState(() => {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const value = params.get("token") || "";

    if (window.location.hash) {
      window.history.replaceState(
        null,
        document.title,
        `${window.location.pathname}${window.location.search}`
      );
    }

    return value;
  });

  const [account, setAccount] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadAccount = async () => {
    try {
      const { data } = await api.post("/auth/setup/validate", {
        token,
      });

      setAccount(data);

      if (data.password_set && !data.mfa_enabled) {
        const result = await api.post("/auth/setup/mfa/start", { token });
        setMfa(result.data);
      }
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          "This setup link is invalid or has expired."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError("Missing setup token.");
      setLoading(false);
      return;
    }

    loadAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submitPassword = async (event) => {
    event.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);

    try {
      await api.post("/auth/setup/password", {
        token,
        password,
      });

      const { data } = await api.post("/auth/setup/mfa/start", { token });

      setMfa(data);
      setAccount((current) => ({
        ...current,
        password_set: true,
      }));
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to set password."
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyMfa = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    try {
      const { data } = await api.post("/auth/setup/mfa/verify", {
        token,
        code,
      });

      setRecoveryCodes(data.recovery_codes || []);
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Unable to verify MFA."
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-lightbg flex items-center justify-center p-6">
        <div className="text-sm text-slate">Loading account setup...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-lightbg px-5 py-10">
      <div className="mx-auto max-w-lg">
        <div className="mb-8 flex justify-center">
          <Logo />
        </div>

        <div className="rounded-md border border-brandborder bg-white p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="rounded-sm bg-navy p-2.5 text-cyan">
              <ShieldCheck size={22} />
            </div>

            <div>
              <h1 className="font-heading text-2xl font-bold text-navy">
                Set up your TWORAY account
              </h1>

              {account?.email && (
                <p className="mt-1 text-sm text-slate">
                  {account.email}
                </p>
              )}
            </div>
          </div>

          {error && (
            <div className="mt-6 rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {error}
            </div>
          )}

          {!error && recoveryCodes.length > 0 && (
            <div className="mt-7">
              <h2 className="font-semibold text-navy">
                Account setup complete
              </h2>

              <p className="mt-2 text-sm text-slate">
                Save these recovery codes securely. Each code can be used once.
              </p>

              <div className="mt-4 grid gap-2 rounded-sm bg-lightbg p-4 font-mono text-sm text-navy">
                {recoveryCodes.map((item) => (
                  <div key={item}>{item}</div>
                ))}
              </div>

              <button
                type="button"
                onClick={() => navigate("/admin/login")}
                className="mt-6 w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white"
              >
                Continue to sign in
              </button>
            </div>
          )}

          {!error &&
            recoveryCodes.length === 0 &&
            account &&
            !account.password_set && (
              <form onSubmit={submitPassword} className="mt-7 space-y-4">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-navy">
                    Create password
                  </label>
                  <input
                    type="password"
                    minLength={12}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-sm border border-brandborder px-3 py-2.5"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-navy">
                    Confirm password
                  </label>
                  <input
                    type="password"
                    minLength={12}
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full rounded-sm border border-brandborder px-3 py-2.5"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  className="w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy ? "Saving..." : "Continue to MFA"}
                </button>
              </form>
            )}

          {!error &&
            recoveryCodes.length === 0 &&
            account?.password_set &&
            mfa && (
              <form onSubmit={verifyMfa} className="mt-7">
                <h2 className="font-semibold text-navy">
                  Set up authenticator MFA
                </h2>

                <p className="mt-2 text-sm text-slate">
                  Scan this QR code with your authenticator app.
                </p>

                <div className="mt-5 flex justify-center rounded-sm bg-white p-4">
                  <QRCodeSVG value={mfa.provisioning_uri} size={190} />
                </div>

                <div className="mt-4 break-all rounded-sm bg-lightbg p-3 text-xs text-slate">
                  Manual key: {mfa.manual_key}
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-sm font-semibold text-navy">
                    6-digit authentication code
                  </label>

                  <input
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    inputMode="numeric"
                    required
                    minLength={6}
                    maxLength={12}
                    className="w-full rounded-sm border border-brandborder px-3 py-2.5"
                  />
                </div>

                <button
                  type="submit"
                  disabled={busy}
                  className="mt-5 w-full rounded-sm bg-navy px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {busy ? "Verifying..." : "Complete account setup"}
                </button>
              </form>
            )}
        </div>
      </div>
    </div>
  );
}
